// src/components/Notifications/NotificationCenter.jsx
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import "./NotificationCenter.scss";
import NotificationBellIcon from "../../assets/Notification_bellIcon1.png";
import { createPortal } from "react-dom";

/**
 * NotificationCenter
 * - Newly Assigned (any new task/todo created since last snapshot)  <-- counts toward badge
 * - Overdue (for current user)                                      <-- displayed but NOT counted
 * - Upcoming (for current user)                                     <-- displayed but NOT counted
 *
 * Snapshot key: allprojects_snapshot
 * Dismissed key: allprojects_dismissed
 */

const SNAPSHOT_KEY = "allprojects_snapshot";
const DISMISSED_KEY = "allprojects_dismissed";

const NotificationCenter = ({ onNavigate, onNotificationReceived }) => {
  const { user } = useAuth();
  const closeTimeoutRef = useRef(null);

  const navigate = useNavigate();

  // currentUser used for overdue/upcoming checks
  const currentUserId = String(
    user?.empId ??
      user?.employeeId ??
      user?.user_id ??
      user?.userId ??
      user?.id ??
      "",
  );

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const bellRef = useRef(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });

  // primary lists
  const [newlyAssigned, setNewlyAssigned] = useState([]); // shows and counts in badge
  const [overdue, setOverdue] = useState([]); // for current user only, NOT counted
  const [upcoming, setUpcoming] = useState([]); // for current user only, NOT counted

  const knownAssignmentsRef = useRef(new Set()); // runtime dedupe
  const lastSnapshotRef = useRef([]); // persisted baseline
  const dismissedRef = useRef(new Set()); // persisted dismissal keys

  // helpers
  const keyForTask = (id) => `task:${id}`;
  const keyForTodo = (id) => `todo:${id}`;
  const toDate = (d) => {
    try {
      return d ? new Date(d) : null;
    } catch {
      return null;
    }
  };
  const openDropdown = () => {
    if (!bellRef.current) return;

    const rect = bellRef.current.getBoundingClientRect();

    setDropdownPos({
      top: rect.bottom + window.scrollY + 8,
      left: rect.right + window.scrollX - 380, // dropdown width
    });

    setOpen(true);
  };

  // format: "22 Nov" and "11:06 AM"
  const formatDateShort = (iso) => {
    const dt = toDate(iso);
    if (!dt) return "";
    const day = dt.getDate();
    const month = dt.toLocaleString("default", { month: "short" });
    return `${day} ${month}`;
  };
  const formatTime = (iso) => {
    const dt = toDate(iso);
    if (!dt) return "";
    return dt.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  };

  const buildNotif = ({
    key,
    type,
    title,
    body,
    projectId,
    projectTitle,
    createdAt,
    link,
  }) => ({
    id: `${key}-${Date.now()}-${Math.floor(Math.random() * 9999)}`,
    key,
    type,
    title,
    body,
    projectId,
    projectTitle,
    createdAt: createdAt || new Date().toISOString(),
    link,
  });

  const showBrowserNotification = (title, body) => {
    if (typeof window === "undefined" || !("Notification" in window)) return;
    if (Notification.permission === "granted") {
      try {
        new Notification(title, { body });
      } catch {}
    }
  };

  // build prev map from snapshot (projectId -> Set(keys)) and include dismissed
  const buildPrevMap = (snapshot = [], dismissed = new Set()) => {
    const m = {};
    (snapshot || []).forEach((p) => {
      const pid = String(p.projectId);
      m[pid] = m[pid] || new Set();
      (p.tasks || []).forEach((t) => {
        if (t.taskId != null) m[pid].add(keyForTask(t.taskId));
        (t.todoRequestDTOList || []).forEach((td) => {
          if (td.toDoId != null) m[pid].add(keyForTodo(td.toDoId));
        });
      });
    });
    // global dismissed pseudo-set
    m["_DISMISSED_"] = new Set(Array.from(dismissed || []));
    return m;
  };

  // load dismissed from storage
  const loadDismissed = () => {
    try {
      const raw = localStorage.getItem(DISMISSED_KEY);
      const arr = raw ? JSON.parse(raw) : [];
      return new Set(Array.isArray(arr) ? arr : []);
    } catch {
      return new Set();
    }
  };

  const persistDismissed = (set) => {
    try {
      localStorage.setItem(DISMISSED_KEY, JSON.stringify(Array.from(set)));
    } catch {}
  };

  // add overdue/upcoming notifs for current user (keeps them separate and not counted)
  const maybeAddDueNotifsForTaskAndTodos = (p, task) => {
    const projectTitle =
      p.projectTitle || p.projectName || `Project ${p.projectId}`;
    const projectId = p.projectId;

    // Task due (only if task owner is current user)
    if (task.taskOwnerId && String(task.taskOwnerId) === currentUserId) {
      const end = toDate(task.endDate);
      if (end) {
        if (end < new Date(new Date().setHours(0, 0, 0, 0))) {
          setOverdue((s) =>
            s.concat(
              buildNotif({
                key: keyForTask(task.taskId),
                type: "overdue",
                title: `Task overdue: ${
                  task.taskTitle || task.taskName || "(untitled)"
                }`,
                body: `${projectTitle}`,
                projectId,
                projectTitle,
                createdAt:
                  task.createdAt ||
                  task.createdDate ||
                  new Date().toISOString(),
                link: `/project/${projectId}?view=table&task=${task.taskId}`,
              }),
            ),
          );
        } else {
          const days = Math.round(
            (end - new Date(new Date().setHours(0, 0, 0, 0))) /
              (1000 * 60 * 60 * 24),
          );
          if (days >= 0 && days <= 3) {
            setUpcoming((s) =>
              s.concat(
                buildNotif({
                  key: keyForTask(task.taskId),
                  type: "upcoming",
                  title: `Task due soon: ${
                    task.taskTitle || task.taskName || "(untitled)"
                  }`,
                  body: `${projectTitle} • ends ${formatDateShort(end)}`,
                  projectId,
                  projectTitle,
                  createdAt:
                    task.createdAt ||
                    task.createdDate ||
                    new Date().toISOString(),
                  link: `/project/${projectId}?view=table&task=${task.taskId}`,
                }),
              ),
            );
          }
        }
      }
    }

    // Todos due (only for todos assigned to current user)
    (task.todoRequestDTOList || []).forEach((todo) => {
      if (todo.assignedEmpId && String(todo.assignedEmpId) === currentUserId) {
        const end = toDate(task.endDate) || toDate(todo.endDate);
        if (end) {
          if (end < new Date(new Date().setHours(0, 0, 0, 0))) {
            setOverdue((s) =>
              s.concat(
                buildNotif({
                  key: keyForTodo(todo.toDoId),
                  type: "overdue",
                  title: `Todo overdue: ${
                    todo.toDoTitle || todo.title || "(untitled)"
                  }`,
                  body: `${task.taskTitle || "(task)"} • ${projectTitle}`,
                  projectId,
                  projectTitle,
                  createdAt:
                    todo.createdAt ||
                    todo.createdDate ||
                    new Date().toISOString(),
                  link: `/project/${projectId}?view=table&task=${task.taskId}&todo=${todo.toDoId}`,
                }),
              ),
            );
          } else {
            const days = Math.round(
              (end - new Date(new Date().setHours(0, 0, 0, 0))) /
                (1000 * 60 * 60 * 24),
            );
            if (days >= 0 && days <= 3) {
              setUpcoming((s) =>
                s.concat(
                  buildNotif({
                    key: keyForTodo(todo.toDoId),
                    type: "upcoming",
                    title: `Todo due soon: ${
                      todo.toDoTitle || todo.title || "(untitled)"
                    }`,
                    body: `${task.taskTitle || "(task)"} • ${projectTitle}`,
                    projectId,
                    projectTitle,
                    createdAt:
                      todo.createdAt ||
                      todo.createdDate ||
                      new Date().toISOString(),
                    link: `/project/${projectId}?view=table&task=${task.taskId}&todo=${todo.toDoId}`,
                  }),
                ),
              );
            }
          }
        }
      }
    });
  };

  // Dismiss functions (new items only)
  const dismissKey = (key) => {
    setNewlyAssigned((s) => s.filter((n) => n.key !== key));
    dismissedRef.current.add(key);
    persistDismissed(dismissedRef.current);
  };
  const clearAllNew = () => {
    newlyAssigned.forEach((n) => dismissedRef.current.add(n.key));
    persistDismissed(dismissedRef.current);
    setNewlyAssigned([]);
  };

  // on click navigate
  const handleNavigate = (notif) => {
    setOpen(false);
    if (notif?.link) navigate(notif.link);
    else if (onNavigate && notif.projectId)
      onNavigate(`project/${notif.projectId}`);
  };

  // --- mount: load snapshot, dismissed, diff window.__ALL_PROJECTS__ and compute lists
  useEffect(() => {
    setLoading(true);
    const projectsNow = window.__ALL_PROJECTS__ || [];

    // load snapshot
    try {
      const raw = localStorage.getItem(SNAPSHOT_KEY);
      lastSnapshotRef.current = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(lastSnapshotRef.current)) lastSnapshotRef.current = [];
    } catch {
      lastSnapshotRef.current = [];
    }

    // load dismissed
    dismissedRef.current = loadDismissed();

    const prevMap = buildPrevMap(lastSnapshotRef.current, dismissedRef.current);

    // reset lists
    setNewlyAssigned([]);
    setOverdue([]);
    setUpcoming([]);
    knownAssignmentsRef.current.clear();

    // pushIfNew: unconditional new (no assignment checks) but skip dismissed
    const pushIfNew = (projectId, key, makeNotif) => {
      const pid = String(projectId);
      const prevSet = prevMap[pid] || new Set();
      const dismissedSet = prevMap["_DISMISSED_"] || new Set();
      knownAssignmentsRef.current.add(key);
      if (!prevSet.has(key) && !dismissedSet.has(key)) {
        const n = makeNotif();
        setNewlyAssigned((s) => [n, ...s]);
        if (onNotificationReceived) onNotificationReceived(n);
        showBrowserNotification(n.title, n.body);
      }
    };

    // walk projects and detect new items and compute due-notifs for current user
    (projectsNow || []).forEach((p) => {
      const projTitle =
        p.projectTitle || p.projectName || `Project ${p.projectId}`;
      (p.tasks || []).forEach((task) => {
        const tKey = keyForTask(task.taskId);
        pushIfNew(p.projectId, tKey, () =>
          buildNotif({
            key: tKey,
            type: "task",
            title: `New task: ${
              task.taskTitle || task.taskName || task.title || "(untitled)"
            }`,
            body: `${projTitle}`,
            projectId: p.projectId,
            projectTitle: projTitle,
            createdAt:
              task.createdAt || task.createdDate || new Date().toISOString(),
            link: `/project/${p.projectId}?view=table&task=${task.taskId}`,
          }),
        );

        // todos
        (task.todoRequestDTOList || []).forEach((todo) => {
          const tdKey = keyForTodo(todo.toDoId);
          pushIfNew(p.projectId, tdKey, () =>
            buildNotif({
              key: tdKey,
              type: "todo",
              title: `New todo: ${
                todo.toDoTitle || todo.title || "(untitled)"
              }`,
              body: `${task.taskTitle || "(task)"} • ${projTitle}`,
              projectId: p.projectId,
              projectTitle: projTitle,
              createdAt:
                todo.createdAt || todo.createdDate || new Date().toISOString(),
              link: `/project/${p.projectId}?view=table&task=${task.taskId}&todo=${todo.toDoId}`,
            }),
          );
        });

        // compute overdue/upcoming for current user (these do not affect badge/new detection)
        maybeAddDueNotifsForTaskAndTodos(p, task);
      });
    });

    // persist fresh snapshot as baseline
    try {
      localStorage.setItem(SNAPSHOT_KEY, JSON.stringify(projectsNow));
      lastSnapshotRef.current = projectsNow;
    } catch (e) {
      console.warn("NotificationCenter: failed to persist snapshot", e);
    }

    setLoading(false);

    // handler for incoming allprojects-ready events -> compute new items relative to previous persisted baseline
    const handler = (ev) => {
      const list = ev?.detail?.projects ?? window.__ALL_PROJECTS__ ?? [];
      if (!Array.isArray(list)) return;

      const prevMapLocal = buildPrevMap(
        lastSnapshotRef.current,
        dismissedRef.current,
      );
      const newNotifs = [];

      (list || []).forEach((p) => {
        const pid = String(p.projectId);
        const prevSet = prevMapLocal[pid] || new Set();
        const dismissedSet = prevMapLocal["_DISMISSED_"] || new Set();

        (p.tasks || []).forEach((task) => {
          const tKey = keyForTask(task.taskId);
          if (!prevSet.has(tKey) && !dismissedSet.has(tKey)) {
            newNotifs.push(
              buildNotif({
                key: tKey,
                type: "task",
                title: `New task: ${
                  task.taskTitle || task.taskName || task.title || "(untitled)"
                }`,
                body: `${p.projectTitle || p.projectName || ""}`,
                projectId: p.projectId,
                projectTitle: p.projectTitle || p.projectName || "",
                createdAt:
                  task.createdAt ||
                  task.createdDate ||
                  new Date().toISOString(),
                link: `/project/${p.projectId}?view=table&task=${task.taskId}`,
              }),
            );
          }

          (task.todoRequestDTOList || []).forEach((todo) => {
            const tdKey = keyForTodo(todo.toDoId);
            if (!prevSet.has(tdKey) && !dismissedSet.has(tdKey)) {
              newNotifs.push(
                buildNotif({
                  key: tdKey,
                  type: "todo",
                  title: `New todo: ${
                    todo.toDoTitle || todo.title || "(untitled)"
                  }`,
                  body: `${task.taskTitle || "(task)"} • ${
                    p.projectTitle || p.projectName || ""
                  }`,
                  projectId: p.projectId,
                  projectTitle: p.projectTitle || p.projectName || "",
                  createdAt:
                    todo.createdAt ||
                    todo.createdDate ||
                    new Date().toISOString(),
                  link: `/project/${p.projectId}?view=table&task=${task.taskId}&todo=${todo.toDoId}`,
                }),
              );
            }
          });
        });
      });

      if (newNotifs.length > 0) {
        setNewlyAssigned((s) => [...newNotifs, ...s]);
        newNotifs.forEach((n) => {
          showBrowserNotification(n.title, n.body);
          if (onNotificationReceived) onNotificationReceived(n);
        });
      }

      // persist incoming list as baseline
      try {
        localStorage.setItem(SNAPSHOT_KEY, JSON.stringify(list));
        lastSnapshotRef.current = list;
      } catch (e) {
        console.warn(
          "NotificationCenter: failed to persist snapshot in handler",
          e,
        );
      }
    };

    window.addEventListener("allprojects-ready", handler);

    // persist snapshot on logout or beforeunload (optional)
    const onLogout = () => {
      try {
        const snapshot =
          window.__ALL_PROJECTS__ || lastSnapshotRef.current || [];
        localStorage.setItem(SNAPSHOT_KEY, JSON.stringify(snapshot));
      } catch {}
    };
    const onBeforeUnload = () => {
      try {
        const snapshot =
          window.__ALL_PROJECTS__ || lastSnapshotRef.current || [];
        localStorage.setItem(SNAPSHOT_KEY, JSON.stringify(snapshot));
      } catch {}
    };
    window.addEventListener("user-logout", onLogout);
    window.addEventListener("beforeunload", onBeforeUnload);

    return () => {
      window.removeEventListener("allprojects-ready", handler);
      window.removeEventListener("user-logout", onLogout);
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onNotificationReceived, currentUserId]);

  // bell badge counts only newlyAssigned (not overdue/upcoming)
  const unreadCount = newlyAssigned.length;
  return (
    <div className="NotificationCenter-root">
      <div
        className="NotificationCenter-hoverArea"
        onMouseEnter={() => {
          // Cancel any scheduled close
          if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = null;
          }
          openDropdown();
        }}
        onMouseLeave={() => {
          // Delay close by 1 second
          closeTimeoutRef.current = setTimeout(() => {
            setOpen(false);
            closeTimeoutRef.current = null;
          }, 300); // 1s delay
        }}
      >
        <button
          ref={bellRef}
          className={`NotificationCenter-bell ${
            unreadCount > 0 ? "has-unread" : ""
          }`}
          aria-label="Notifications"
          onClick={() => setOpen((v) => !v)}
          title="Notifications"
        >
          <img
            src={NotificationBellIcon}
            alt="Notification Bell"
            className={`NotificationCenter-bellIcon ${
              unreadCount > 0 ? "bell--ringing" : ""
            }`}
          />

          {unreadCount > 0 && (
            <span className="NotificationCenter-badge">{unreadCount}</span>
          )}
        </button>

        {open &&
          createPortal(
            <div
              className="NotificationCenter-dropdown"
              style={{
                position: "absolute",
                top: dropdownPos.top,
                left: dropdownPos.left,
                zIndex: 99999,
              }}
              role="dialog"
              aria-label="Notifications"
            >
              <div className="NotificationCenter-header">
                <div className="NotificationCenter-title">Newly Assigned</div>
                <div className="NotificationCenter-controls">
                  <button
                    className="NotificationCenter-clear-all"
                    onClick={clearAllNew}
                  >
                    Clear all
                  </button>
                </div>
              </div>

              <div className="NotificationCenter-body">
                {loading && (
                  <div className="NotificationCenter-loading">Loading...</div>
                )}

                {newlyAssigned.length === 0 && !loading && (
                  <div className="NotificationCenter-empty">No new tasks</div>
                )}

                {newlyAssigned.map((n) => (
                  <div
                    key={n.id}
                    className={`NotificationCenter-item NotificationCenter-item-${n.type}`}
                    onMouseEnter={(e) => e.currentTarget.classList.add("hover")}
                    onMouseLeave={(e) =>
                      e.currentTarget.classList.remove("hover")
                    }
                  >
                    <div
                      className="NotificationCenter-item-main"
                      onClick={() => handleNavigate(n)}
                    >
                      <div className="NotificationCenter-item-title">
                        {n.title}
                      </div>
                      <div className="NotificationCenter-item-sub">
                        {n.body}
                      </div>
                      <div className="NotificationCenter-item-meta">
                        <span className="NotificationCenter-item-date">
                          {formatDateShort(n.createdAt)}
                        </span>
                        <span className="NotificationCenter-item-time">
                          {formatTime(n.createdAt)}
                        </span>
                      </div>
                    </div>

                    <button
                      className="NotificationCenter-item-dismiss"
                      aria-label="Dismiss notification"
                      onClick={() => dismissKey(n.key)}
                      title="Dismiss"
                    >
                      ✕
                    </button>
                  </div>
                ))}

                <div className="NotificationCenter-section">
                  <div className="NotificationCenter-section-title">
                    Overdue ({overdue.length})
                  </div>
                  {overdue.length === 0 && (
                    <div className="NotificationCenter-empty-line">
                      No overdue items
                    </div>
                  )}
                  {overdue.map((n) => (
                    <div
                      key={n.id}
                      className="NotificationCenter-item overdue"
                      onClick={() => handleNavigate(n)}
                    >
                      <div className="NotificationCenter-item-main">
                        <div className="NotificationCenter-item-title">
                          {n.title}
                        </div>
                        <div className="NotificationCenter-item-sub">
                          {n.body}
                        </div>
                        <div className="NotificationCenter-item-meta">
                          <span className="NotificationCenter-item-date">
                            {formatDateShort(n.createdAt)}
                          </span>
                          <span className="NotificationCenter-item-time">
                            {formatTime(n.createdAt)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="NotificationCenter-section">
                  <div className="NotificationCenter-section-title">
                    Upcoming ({upcoming.length})
                  </div>
                  {upcoming.length === 0 && (
                    <div className="NotificationCenter-empty-line">
                      No upcoming items
                    </div>
                  )}
                  {upcoming.map((n) => (
                    <div
                      key={n.id}
                      className="NotificationCenter-item upcoming"
                      onClick={() => handleNavigate(n)}
                    >
                      <div className="NotificationCenter-item-main">
                        <div className="NotificationCenter-item-title">
                          {n.title}
                        </div>
                        <div className="NotificationCenter-item-sub">
                          {n.body}
                        </div>
                        <div className="NotificationCenter-item-meta">
                          <span className="NotificationCenter-item-date">
                            {formatDateShort(n.createdAt)}
                          </span>
                          <span className="NotificationCenter-item-time">
                            {formatTime(n.createdAt)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* <div className="NotificationCenter-footer">
          <small className="NotificationCenter-version">Live</small>
        </div> */}
            </div>,
            document.body,
          )}
      </div>
    </div>
  );
};

export default NotificationCenter;
